import { useLazyQuery } from '@apollo/client'
import { isEmpty } from 'lodash'
import { ReactElement, useState } from 'react'
import { defineMessages, useIntl } from 'react-intl'

import { Autocomplete, useDebouncedQuery } from 'components/v2/Autocomplete'
import { IInputProps } from 'components/Input'
import { GTVehicleSuggestion, GTVehicleSuggestionsResponse } from 'gql/types.generated'
import { GET_VEHICLES_SUGGESTIONS } from 'features/Vehicles/queries-legacy'

const messages = defineMessages({
  error: {
    id: 'vehiclesV2.autocomplete.error',
    defaultMessage: 'There was an issue while loading your vehicles'
  },
  empty: {
    id: 'vehiclesV2.autocomplete.empty',
    defaultMessage: 'No vehicles found'
  }
})

interface IVehicleAutocompleteProps extends Omit<IInputProps<HTMLInputElement>, 'onChange'> {
  onChange: (vehicle: GTVehicleSuggestion) => void
  companyId?: string
  isActive?: boolean
}

export const VehicleAutocomplete = ({
  onChange,
  companyId,
  isActive,
  ...props
}: IVehicleAutocompleteProps): ReactElement => {
  const intl = useIntl()
  const [pattern, setPattern] = useState('')
  const [getVehicles, { data, loading, error }] = useLazyQuery<{
    vehicleSuggestions: GTVehicleSuggestionsResponse
  }>(GET_VEHICLES_SUGGESTIONS)
  const { isDebouncing, shouldShowItems } = useDebouncedQuery({
    query: getVehicles,
    pattern,
    extraInputs: {
      companyID: companyId || null,
      isEnabled: isActive
    }
  })

  const shownItems = data?.vehicleSuggestions.items || []

  const handleChange = (vehicle: GTVehicleSuggestion, { inputValue = '' } = {}): void => {
    setPattern(inputValue)

    if (!isEmpty(vehicle)) {
      onChange(vehicle)
    } else {
      onChange({
        id: '',
        name: '',
        capacity: 0,
        externalID: ''
      })
    }
  }

  return (
    <Autocomplete
      {...props}
      items={shownItems}
      getItemLabel={(vehicle: GTVehicleSuggestion): string => {
        let label = vehicle.name
        if (vehicle.externalID) {
          label += ` (${vehicle.externalID})`
        }
        return label
      }}
      loading={loading || isDebouncing}
      error={error}
      errorMessage={intl.formatMessage(messages.error)}
      emptyMessage={intl.formatMessage(messages.empty)}
      onChange={handleChange}
      shouldShowItems={shouldShowItems}
    />
  )
}
